import React from "react";
import FeeYearCard from "./FeeYearCard";
import { getCurrentAcademicYear } from "../../utils/academicYears";

// Map the real yearStage code (from the enrollment) to a readable label.
const STAGE_NAMES = {
  FY: "First Year",
  SY: "Second Year",
  TY: "Third Year",
  IV: "Fourth Year",
  V: "Fifth Year",
};

// Prefer the ACTUAL year-stage from the enrollment; fall back to just the academic
// year when it's unknown. Never fabricate an ordinal from list position — that
// mislabelled promoted students (e.g. an SY fee shown as "First Year").
const tuitionTitle = (fee) => {
  const stage = fee.yearStage ? STAGE_NAMES[fee.yearStage] || fee.yearStage : null;
  return stage ? `${stage} — ${fee.academicYear}` : `${fee.academicYear}`;
};

/**
 * Vertical fee timeline for the student "My Fees" page.
 *
 * Node 0 (optional) = admission / First-Year application fee, shown only when
 * there is an outstanding balance OR it has been paid. Subsequent nodes = one
 * per tuition year from getAllStudentFees, sorted chronologically (FY -> current)
 * so the timeline reads oldest to newest.
 *
 * @param {object|null} applicationFeeData  getStudentApplicationFee().data
 * @param {Array}       tuitionFees         getAllStudentFees().data
 * @param {boolean}     paymentLoading
 * @param {Function}    onPayTuition
 * @param {Function}    onPayApplication
 */
const FeeTimeline = ({
  applicationFeeData,
  tuitionFees = [],
  paymentLoading = false,
  onPayTuition,
  onPayApplication,
}) => {
  // Admission node: render when paid, or when there is a pending balance.
  const showAdmission =
    applicationFeeData &&
    (applicationFeeData.paymentStatus === "paid" ||
      (applicationFeeData.remainingAmount ?? 0) > 0 ||
      (applicationFeeData.paidAmount ?? 0) > 0);

  // Ascending by academic year ("YYYY-YY" sorts lexicographically as expected).
  const sortedTuition = [...(tuitionFees || [])].sort((a, b) =>
    (a.academicYear || "").localeCompare(b.academicYear || ""),
  );

  const currentYear = getCurrentAcademicYear();

  const nodes = [];

  if (showAdmission) {
    nodes.push({
      key: `admission-${applicationFeeData.applicationId}`,
      kind: "admission",
      data: applicationFeeData,
      title: "Admission (First Year)",
      defaultExpanded: (applicationFeeData.remainingAmount ?? 0) > 0,
    });
  }

  sortedTuition.forEach((fee) => {
    nodes.push({
      key: fee._id || `tuition-${fee.academicYear}`,
      kind: "tuition",
      data: fee,
      title: tuitionTitle(fee),
      // Auto-expand the current year so its payment options are ready to hand.
      defaultExpanded: fee.academicYear === currentYear,
    });
  });

  if (nodes.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-500">No fee records found for your account.</p>
      </div>
    );
  }

  return (
    <ol className="relative border-s-2 border-gray-200 ms-3 space-y-8">
      {nodes.map((node) => (
        <li key={node.key} className="ms-6">
          {/* Timeline dot */}
          <span
            className={`absolute -start-[9px] mt-5 h-4 w-4 rounded-full ring-4 ring-white ${
              (node.data.remainingAmount ?? 0) > 0
                ? "bg-red-400"
                : node.kind === "admission"
                  ? "bg-orange-400"
                  : "bg-green-500"
            }`}
          />
          <FeeYearCard
            kind={node.kind}
            data={node.data}
            title={node.title}
            defaultExpanded={node.defaultExpanded}
            paymentLoading={paymentLoading}
            onPayTuition={onPayTuition}
            onPayApplication={onPayApplication}
          />
        </li>
      ))}
    </ol>
  );
};

export default FeeTimeline;
